<script setup lang="ts">
import BaseButton from '@/components/BaseButton.vue'
import BaseDetails from '@/components/BaseDetails.vue'
import BaseInput from '@/components/BaseInput.vue'
import { supabase } from '@/composables/supabaseClient'
import toastThrowError from '@/helpers/toastThrowError'
import uuidv7 from '@/helpers/uuidv7'
import router from '@/router'
import { useDatabaseStore } from '@/stores/databaseStore'
import type { Tables } from '@/types/database.types'
import { computed, ref } from 'vue'
import { RouterLink } from 'vue-router'
import type { TableColumn } from './BaseTable.vue'
import BaseTable from './BaseTable.vue'

const databaseStore = useDatabaseStore()

const divisions = computed(() => {
  return databaseStore.assembyDivisions?.map((division) => {
    return {
      id: division.id,
      name: division.name,
      code: division.code,
      assemblies: databaseStore.assemblies?.filter((a) => a.assembly_division_id === division.id),
    }
  })
})

const tableColumns: TableColumn<Tables<'assembly'>>[] = [
  {
    name: 'Code',
    columnDataKey: 'code',
    defaultWidth: 75,
  },
  {
    name: 'Name',
    columnDataKey: 'name',
    defaultWidth: 150,
  },
  {
    name: 'Unit',
    columnDataKey: 'unit_of_measure_id',
    textDisplayFunction: (value) =>
      databaseStore.unitsOfMeasure?.find((uom) => uom.id === value)?.code ?? '',
    defaultWidth: 75,
  },
]

const showNewDivisionInput = ref(false)
const newDivisionInput = ref({ code: '', name: '' })

async function createAssemblyDivision(input: { code: string; name: string }) {
  if (newDivisionInput.value.code === '' || newDivisionInput.value.name === '') {
    throw toastThrowError('Code and Name are required')
  }
  const result = await supabase
    .from('assembly_division')
    .insert({
      id: uuidv7(),
      code: input.code,
      name: input.name,
    })
    .select()
    .single()
  if (result.error) throw toastThrowError('Unable to add division', result.error)
  databaseStore.assembyDivisions?.push(result.data)
  newDivisionInput.value.code = ''
  newDivisionInput.value.name = ''
  showNewDivisionInput.value = false
}
</script>

<template>
  <div class="flex h-full w-full flex-col gap-3 p-3">
    <BaseDetails
      v-for="division in divisions"
      :key="division.id"
      :name="`${division.code} ${division.name}`"
    >
      <div class="flex w-full flex-col gap-3 p-3">
        <BaseTable
          :rows="division.assemblies ?? []"
          row-key="id"
          :columns="tableColumns"
          table-name="databaseAssemblies"
          @row-dbl-click="
            async (row) => {
              const failure = await router.push({
                name: 'DatabaseAssemblyEdit',
                params: { assemblyId: row.id },
              })
            }
          "
        />
        <RouterLink :to="{ name: 'DatabaseAssemblyCreate', params: { divisionId: division.id } }">
          <BaseButton>New Assembly</BaseButton>
        </RouterLink>
      </div>
      <!-- Create new button -->
    </BaseDetails>
    <div v-if="showNewDivisionInput" class="flex flex-col gap-3">
      <div class="flex gap-3">
        <div class="w-1/3">
          <BaseInput v-model="newDivisionInput.code" placeholder="Code..." />
        </div>
        <div class="w-2/3">
          <BaseInput v-model="newDivisionInput.name" placeholder="Name..." />
        </div>
      </div>
      <div class="flex gap-3">
        <BaseButton
          status="secondary"
          @click="
            () => {
              newDivisionInput.code = ''
              newDivisionInput.name = ''
              showNewDivisionInput = false
            }
          "
          >Cancel</BaseButton
        >
        <BaseButton @click="createAssemblyDivision(newDivisionInput)">Save</BaseButton>
      </div>
    </div>
    <BaseButton v-else @click="showNewDivisionInput = true">New Division</BaseButton>
  </div>
</template>
